import { firestore } from "firebase";
import { RecordInfo } from "./RecordInfo";
import ProjectOverview from "./ProjectOverview";
import LeakDetectionProjectOverview from "./LeakDetectionProjectOverview";
import { ApprovalResult, ProjectConclusionState, RepairAction, containsDryingInfo, doesProjectTypeIdHaveDrying, isLeakDetectionOnlyProjectTypeId } from "./ProjectDetails";
import { doesClientHaveApp } from "../repository/ClientRepository";
import { ProjectStepsContainer, getProjectStepContainer } from "./ProjectSteps";
import { AppointmentType } from "./ProjectState";
import { ProjectType } from "../repository/ProjectRepository";
import Vue from "vue";

export enum DamagePatternConclusionState {
  ON_HOLD = "ON_HOLD",
  NO_DRYING_NECESSARY = "NO_DRYING_NECESSARY",
  CARRY_OUT_DRYING = "CARRY_OUT_DRYING",
  NOT_STARTED = "NOT_STARTED",
  NO_CONTRACT = "NO_CONTRACT",
  UNDEFINED = "UNDEFINED"
}

export interface AppointmentInfo {
  createdBy: string;
  creationTimestamp: string;
  appointmentDate: string;
  completed: boolean;
  responsibleUser?: string;
  /**
   * Additional (team) users assisting the main `responsibleUser` on this appointment.
   * Raw identifiers, same convention as `responsibleUser` (staffNumber/nickname/userId).
   */
  additionalUsers?: string[];
  note?: string;
  appointmentType?: AppointmentType;
  apartmentNumber?: number;
  contactPersonNumber?: number;
}

export function isTimeOfAppointmentSet(appointmentDate: Date) {
  return appointmentDate.getMinutes() != 0 || appointmentDate.getHours() != 0;
}

export enum NextDryingAction {
  MEASUREMENT = "MEASUREMENT",
  DRYING_CONCLUSION = "DRYING_CONCLUSION",
  FOLLOW_UP_INSPECTION = "FOLLOW_UP_INSPECTION",
}

export interface DryingProjectOverview extends ProjectOverview {
  numRooms: number;
  /**
   * Distinct devices currently on site: panel and generic assignments whose
   * `endDate` is still open. Devices that were picked up again are not counted,
   * so this drops back to 0 once the last device leaves the project.
   */
  numDevices: number;
  /**
   * Distinct devices the project has used at any point, open assignments and
   * ended ones alike. A device assigned, released and assigned again counts
   * once.
   *
   * Written by IRES Cloud on every device assignment change. Existing overview
   * entries were deliberately not backfilled, so it is absent on any project
   * whose devices have not changed since the field was introduced, and callers
   * must fall back to `numDevices`.
   */
  numDevicesTotal?: number;
  numContactPersons: number;
  assignedPanels: Array<string>;
  allAssignmentsClosed?: boolean;
  damagePatternStartedBy?: RecordInfo;
  projectStartedBy?: RecordInfo;
  damagePatternCompletedBy?: RecordInfo;
  damagePatternTechnicianId?: string;
  plannedDamageAssessmentDate?: firestore.Timestamp;
  plannedDamageAssessmentTime?: firestore.Timestamp;
  earliestIresDeviceAssignmentStartDate?: firestore.Timestamp;
  followUpInspectionDate?: firestore.Timestamp;
  nextDryingAction?: NextDryingAction;
  internalProjectNumber: number;
  lossAdjusterId?: string;
  setupDryingStartedBy?: RecordInfo;
  packupDevicesFinishedBy?: RecordInfo;
  dryingEnd?: firestore.Timestamp;
  projectConclusionFinishedBy?: RecordInfo | null;
  lastTimeChanged?: RecordInfo;
  documentationAndLeakDetectionComplete?: boolean;
  clientDataComplete?: boolean;
  damagePatternComplete?: boolean;
  damagePatternConclusionComplete?: boolean;
  dryingSetupComplete?: boolean;
  deviceSetupComplete?: boolean;
  dryingProgressComplete?: boolean | null;
  devicePackupComplete?: boolean;
  projectConclusionComplete?: boolean;
  estimatedDryingDurationInDays?: number;
  dryingSuccessful: boolean | null;
  damagePatternConclusionState: DamagePatternConclusionState;
  repairAction?: RepairAction;
  repairCompletedBy?: RecordInfo;
  repairFinishedBy?: RecordInfo;
  dryingProjectSteps?: ProjectStepsContainer;
}

export function getProjectOverviewEndDateLegacy(
  projectOverview: DryingProjectOverview | LeakDetectionProjectOverview
) {
  if (
    projectOverview.projectApproval?.approvalResult ===
    ApprovalResult.REJECTED
  ) {
    return projectOverview.projectApproval?.recordInfo?.recordedAt;
  }

  if (containsDryingInfo(projectOverview)) {
    const dryingProjectOverview = projectOverview as DryingProjectOverview;

    if (dryingProjectOverview) {
      if (dryingProjectOverview.projectConclusionComplete) {
        return projectOverview.endDate;
      }
    }

    if (projectOverview.projectState && projectOverview.projectState !== null) {
      if (
        projectOverview.projectState === ProjectConclusionState.RESTORED &&
        dryingProjectOverview.lastOperationAt
      ) {
        return dryingProjectOverview.lastOperationAt;
      }

      return dryingProjectOverview.endDate;
    }

    if (dryingProjectOverview.damagePatternConclusionComplete == true) {
      const damagePatternConclusionState =
        dryingProjectOverview.damagePatternConclusionState;

      if (
        damagePatternConclusionState === DamagePatternConclusionState.NO_DRYING_NECESSARY ||
        damagePatternConclusionState === DamagePatternConclusionState.NO_CONTRACT
      ) {
        return dryingProjectOverview.endDate;
      }
    }

    return doesClientHaveApp() ? null : projectOverview.endDate;
  }

  return projectOverview.endDate;
}

export function getProjectOverviewEndDate(
  projectOverview: DryingProjectOverview | LeakDetectionProjectOverview
) {
  let endDate = getProjectOverviewEndDateLegacy(projectOverview);
  const projectConclusion = projectOverview.conclusion;

  if (
    projectConclusion &&
    projectConclusion !== null
  ) {
    const projectConclusionDate = projectConclusion.conclusionDate;

    if (projectConclusionDate) {
      endDate = projectConclusionDate;
    }
  }

  return endDate;
}

export function calculateDryingProgressPercent(
  overview: DryingProjectOverview | LeakDetectionProjectOverview
): number {
  const dryingProjectOverview = overview as DryingProjectOverview;
  const estimatedDryingDurationInDays = dryingProjectOverview.estimatedDryingDurationInDays ??
    dryingProjectOverview.dryingProjectSteps?.damageAssessmentResult?.estimatedDryingTime;

  if (!dryingProjectOverview) {
    return 0.0;
  }

  if (!estimatedDryingDurationInDays) {
    return 0.0;
  }

  if (!dryingProjectOverview.setupDryingStartedBy) {
    return 0.0;
  }

  if (dryingProjectOverview.dryingEnd) {
    return 1;
  }

  const end = new Date();
  const start = dryingProjectOverview.setupDryingStartedBy.recordedAt.toDate();

  const timeBetweenInMs = end.getTime() - start.getTime();

  const estimatedDryingDurationInMs =
    24 * 60 * 60 * 1000 * estimatedDryingDurationInDays;

  return timeBetweenInMs / estimatedDryingDurationInMs;
}

export function getAllOpenAppointments(projectOverview: ProjectOverview) {
  const appointments: AppointmentInfo[] = [];

  const dryingProjectSteps = getProjectStepContainer(projectOverview);

  if (dryingProjectSteps) {
    const leakDetectionAppointment =
      dryingProjectSteps.leakDetectionAppointment;

    if (leakDetectionAppointment) {
      const appointmentInfo = leakDetectionAppointment as AppointmentInfo;
      appointmentInfo.appointmentType = AppointmentType.LEAK_DETECTION;
      appointments.push(appointmentInfo);
    }

    const damageAssessmentAppointment =
      dryingProjectSteps.damageAssessmentAppointment;

    if (damageAssessmentAppointment) {
      const appointmentInfo = damageAssessmentAppointment as AppointmentInfo;
      appointmentInfo.appointmentType = AppointmentType.DAMAGE_ASSESSMENT;
      appointments.push(appointmentInfo);
    }

    const dryingSetupAppointments = dryingProjectSteps.dryingSetupAppointments;
    if (Array.isArray(dryingSetupAppointments)) {
      dryingSetupAppointments.forEach((appointment) => {
        const appointmentInfo = appointment as AppointmentInfo;
        appointmentInfo.appointmentType = AppointmentType.PREPARE_DRYING;
        appointments.push(appointmentInfo);
      });
    }

    const measurementAppointments =
      dryingProjectSteps.intermediateMeasurementAppointments;

    if (Array.isArray(measurementAppointments)) {
      measurementAppointments.forEach((appointment) => {
        const appointmentInfo = appointment as AppointmentInfo;
        appointmentInfo.appointmentType =
          AppointmentType.INTERMEDIATE_MEASUREMENT;
        appointments.push(appointmentInfo);
      });
    }

    const dryingConclusionAppointments =
      dryingProjectSteps.dryingConclusionAppointments;
    if (Array.isArray(dryingConclusionAppointments)) {
      dryingConclusionAppointments.forEach((appointment) => {
        const appointmentInfo = appointment as AppointmentInfo;
        appointmentInfo.appointmentType = AppointmentType.DRYING_CONCLUSION;
        appointments.push(appointmentInfo);
      });
    }

    const repairAppointments = dryingProjectSteps.repairAppointments;
    if (Array.isArray(repairAppointments)) {
      repairAppointments.forEach((appointment) => {
        const appointmentInfo = appointment as AppointmentInfo;
        appointmentInfo.appointmentType = AppointmentType.REPAIR;
        appointments.push(appointmentInfo);
      });
    }
  }

  return appointments.filter((appointmentInfo) => !appointmentInfo.completed);
}

export function doesContainDryingInfo(
  projectOverview: DryingProjectOverview
) {
  if (projectOverview.projectTypeId) {
    return doesProjectTypeIdHaveDrying(projectOverview.projectTypeId);
  }

  return projectOverview.projectScope.some(type => type === ProjectType.DRYING);
}

export function isLeakDetectionOnly(
  projectOverview: DryingProjectOverview
) {
  if (projectOverview.projectTypeId) {
    return isLeakDetectionOnlyProjectTypeId(projectOverview.projectTypeId);
  }

  return !doesContainDryingInfo(projectOverview);
}

export function isProjectOverviewFinished(
  projectOverview: DryingProjectOverview
) {
  if (projectOverview.endDate === null) {
    return false;
  }

  if (
    projectOverview.projectConclusionComplete ||
    projectOverview.projectApproval?.approvalResult === ApprovalResult.REJECTED ||
    projectOverview.damagePatternConclusionState === DamagePatternConclusionState.NO_DRYING_NECESSARY ||
    projectOverview.damagePatternConclusionState === DamagePatternConclusionState.NO_CONTRACT ||
    !isLeakDetectionOnly(projectOverview)
  ) {
    return true;
  }

  return false;
}

export function getDamagePatternConclusionStateString(
  vue: Vue,
  damagePatternConclusionState: DamagePatternConclusionState,
  hasDrying = true
) {
  switch (damagePatternConclusionState) {
    case DamagePatternConclusionState.ON_HOLD: {
      return vue.$t("dryingProjectOverview.damagePatternConclusionStates.onHold").toString();
    }
    case DamagePatternConclusionState.NO_DRYING_NECESSARY: {
      return vue.$t(`dryingProjectOverview.damagePatternConclusionStates.${hasDrying ? "noDryingNecessary" : "noRestorationNecessary"}`).toString();
    }
    case DamagePatternConclusionState.CARRY_OUT_DRYING: {
      return vue.$t(`dryingProjectOverview.damagePatternConclusionStates.${hasDrying ? "carryOutDrying" : "carryOutRestoration"}`).toString();
    }
    case DamagePatternConclusionState.NO_CONTRACT: {
      return vue.$t("dryingProjectOverview.damagePatternConclusionStates.noContract").toString();
    }

    default: {
      return "";
    }
  }
}